import React, { useState } from 'react';
import Modal from './Modal';
import Button from './Button';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  description?: React.ReactNode;
  /** Extra body content below the description */
  children?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive styling for confirm */
  destructive?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  /** Block dismiss while loading */
  closeOnOverlayClick?: boolean;
}

/**
 * Shared confirmation / destructive confirmation.
 * Action order: Cancel (secondary) then Confirm (primary or danger).
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  loadingLabel,
  onConfirm,
  onCancel,
  closeOnOverlayClick = true,
}) => {
  const [busy, setBusy] = useState(false);
  const isBusy = loading || busy;

  const handleConfirm = async () => {
    if (isBusy) return;
    try {
      setBusy(true);
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isBusy) onCancel();
      }}
      title={title}
      description={typeof description === 'string' ? description : undefined}
      size="sm"
      closeOnOverlayClick={closeOnOverlayClick && !isBusy}
      closeOnEscape={!isBusy}
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isBusy} className="min-h-[44px]">
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={destructive ? 'danger' : 'primary'}
            onClick={() => void handleConfirm()}
            loading={isBusy}
            loadingLabel={loadingLabel || (destructive ? 'Deleting…' : 'Working…')}
            className="min-h-[44px]"
          >
            {confirmLabel}
          </Button>
        </div>
      }
    >
      {typeof description !== 'string' && description ? (
        <div className="text-sm text-gray-300 mb-2">{description}</div>
      ) : null}
      {children}
    </Modal>
  );
};

export default ConfirmDialog;
